import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import type { CreateNewClientInput } from '@/domain/client';

import styles from './ModalAddClient.module.scss';
export interface NewClientProps {
    onSubmitted: (ev: CreateNewClientInput) => void;
}
function ModalAddClient({ onSubmitted }: NewClientProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const toggle = () => setOpen(!open);

    const [formData, setFormData] = useState<{
        name: string;
    }>();

    function updateForm<T extends keyof CreateNewClientInput>(key: T) {
        return (value: CreateNewClientInput[T]) =>
            setFormData((formData) => ({ ...formData, [key]: value }));
    }

    const isValid = !!formData?.name;
    const onFormDataOnSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
        if (isValid) {
            onSubmitted({
                name: formData.name,
            });
            setOpen(false);
        }
        ev.preventDefault();
    };
    return (
        <>
            <Button
                id="openAddClientModal"
                data-testid="openAddClientModal"
                onClick={() => setOpen(true)}
                className={styles.openButton}>
                {t('molecules.modalAddClient.open')}
            </Button>
            <ModalComponent
                isOpen={open}
                onClose={toggle}
                centered={false}
                title={t('molecules.modalAddClient.title')}>
                <form onSubmit={onFormDataOnSubmit}>
                    <ModalBody>
                        <FormField
                            label={t('molecules.modalAddClient.clientName')}
                            htmlFor="ContractDescription">
                            <TextField
                                id="ContractDescription"
                                name="ContractDescription"
                                type="text"
                                value={formData?.name ?? ''}
                                onChange={(ev) => updateForm('name')(ev.target.value)}
                                placeholder={''}
                                required={false}
                            />
                        </FormField>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="secondary" onClick={() => setOpen(false)}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button variant="primary" type="submit" disabled={!isValid}>
                            {t('common.actions.add')}
                        </Button>
                    </ModalFooter>
                </form>
            </ModalComponent>
        </>
    );
}

export default ModalAddClient;
export { ModalAddClient };
